import { useApplicationContext } from '@/context/v1/Application/context';
import { useKimLhc } from '@/context/KimLhc';
import { useStepMutation } from '@/components/views/Report/KimLhc/hooks/useStepMutation';

import { FormFields, FieldNamesEnum } from '@/components/views/Report/KimLhc/types';
import { useUpdateStepAndCommentKimLhcReport } from '@/hooks/useUpdateStepAndCommentKimLhcReport';

interface FrequencyParams {
	id: string;
	file_id: string;
	company_id: string;
	organization_id: string;
	frequency?: number | null;
	weight?: number | null;
	inclined_weight?: boolean | null;
	transporting_cargo_in_pairs?: boolean | null;
	cargo_handling_condition?: string | null;
}

const FREQUENCY_FIELD_PATHS: [string, string][] = [
	[FieldNamesEnum.FREQUENCY, 'frequency'],
	[FieldNamesEnum.FREQUENCY, 'weight'],
	[FieldNamesEnum.FREQUENCY, 'inclined_weight'],
	[FieldNamesEnum.FREQUENCY, 'transporting_cargo_in_pairs'],
	[FieldNamesEnum.FREQUENCY, 'cargo_handling_condition']
];

export const useFrequency = () => {
	const { file_id, report_id, form, onNextStep, currentStep } = useKimLhc();
	const { organization, company } = useApplicationContext();
	const { mutate, isLoading: isLoadingStepMutation } = useStepMutation<FrequencyParams>();
	const { mutateAsync: updateStepAndCommentKimLhcReport, isLoading: isLoadingUpdateStepAndComment } =
		useUpdateStepAndCommentKimLhcReport();

	const buildUpdatePayload = (values: FormFields): FrequencyParams | null => {
		if (!report_id || !file_id) {
			return null;
		}

		const frequencyData = values.frequency ?? {};
		const basePayload: FrequencyParams = {
			id: report_id,
			file_id: file_id,
			company_id: company.id,
			organization_id: organization.id
		};

		const optionalFields = {
			frequency: frequencyData.frequency,
			weight: frequencyData.weight,
			inclined_weight: frequencyData.inclined_weight,
			transporting_cargo_in_pairs: frequencyData.transporting_cargo_in_pairs,
			cargo_handling_condition: frequencyData.cargo_handling_condition
		};

		const filteredOptionalFields = Object.fromEntries(
			Object.entries(optionalFields).filter(([, value]) => value !== null && value !== undefined)
		) as Partial<FrequencyParams>;

		return {
			...basePayload,
			...filteredOptionalFields
		};
	};

	function hasChanges(): boolean {
		return FREQUENCY_FIELD_PATHS.some(path => form.isFieldTouched(path));
	}

	const handleSubmit = async (values: FormFields): Promise<void> => {
		if (!report_id || !hasChanges()) {
			await updateStepAndCommentKimLhcReport({
				id: report_id!,
				file_id: file_id!,
				step: currentStep + 1,
				organization_id: organization.id,
				company_id: company.id
			});
			onNextStep();
			return;
		}

		const payload = buildUpdatePayload(values);
		if (!payload) {
			return;
		}

		await mutate(payload);
	};

	const isLoading = isLoadingStepMutation || isLoadingUpdateStepAndComment;

	return {
		handleSubmit,
		isLoading
	};
};
